import { useQuery, gql } from "utils/urql";
import { HomepageLatestQuery, LatestArticleFragment } from "types/api";
import SectionHeading from "components/homepage/SectionHeading";
import Image from "../Image";
import HomepageMetadata from "./HomepageMetadata";
import styles from "./Latest.module.scss";
import NativeAd from "../ads/Native";
import cx from "classnames";

const query = gql`
  fragment latestAuthor on Author {
    id
    displayName
    url
    slug
  }

  fragment latestArticle on PromoItem {
    id
    title
    url
    datePublished
    authors {
      ...latestAuthor
    }
    squareImage: riverImage(width: 86, height: 86, sizes: ["86w", "172w"]) {
      altText
      srcSet
      url
      width
      height
    }
    wideImage: riverImage(
      width: 200
      height: 133
      sizes: ["148w", "194w", "296w", "388w"]
    ) {
      srcSet
    }
  }

  query HomepageLatest {
    latest {
      id
      slug
      displayName
      url
      river(first: 20) {
        edges {
          node {
            ...latestArticle
          }
        }
      }
    }
  }
`;

const LatestArticle = ({
  article,
  idx,
}: {
  article: LatestArticleFragment;
  idx: number;
}) => {
  return (
    <article className={styles.article} key={article.id}>
      <a
        href={article.url}
        data-action={`click link - latest - image ${idx + 1}`}
      >
        <picture>
          {article.wideImage && (
            <source
              media="(min-width: 576px)"
              srcSet={article.wideImage.srcSet}
              sizes="(min-width: 786px) 194px, 148px"
            ></source>
          )}
          {article.squareImage && (
            <Image
              lazy
              className={styles.image}
              src={article.squareImage.url}
              srcSet={article.squareImage.srcSet}
              alt={article.squareImage.altText}
              width={article.squareImage.width}
              height={article.squareImage.height}
              sizes="86px"
            />
          )}
        </picture>
      </a>

      <div className={styles.articleInfo}>
        <a
          href={article.url}
          className={styles.link}
          data-action={`click link - latest - title ${idx + 1}`}
        >
          <h3
            className={styles.title}
            dangerouslySetInnerHTML={{
              __html: article.title,
            }}
          ></h3>
        </a>
        <HomepageMetadata
          authors={article.authors}
          datePublished={article.datePublished}
          analyticsSection="latest"
          riverIndex={idx + 1}
        />
      </div>
    </article>
  );
};

const Latest = () => {
  const [{ data }] = useQuery<HomepageLatestQuery>({
    query,
  });

  if (!data) return null;

  const articles = data.latest.river.edges;

  return (
    <section data-testid="homepage-latest">
      <SectionHeading
        analyticsSlug="latest"
        secondaryLinkText="see all"
        title={data.latest.displayName}
        url={data.latest.url}
      />
      <ol className={styles.ol}>
        {articles.map((article, idx) => {
          return (
            <li key={article.node.id} className={styles.li}>
              <LatestArticle article={article.node} idx={idx} />
              {(idx === 2 || idx === 9) && (
                <NativeAd
                  key={`ads-${idx}`}
                  className={cx(
                    styles.native,
                    "s-native s-native--standard s-native--disable-border"
                  )}
                />
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export default Latest;
